import * as THREE from 'three';
import { gsap } from 'gsap/all';
import { fragment, vertex } from './shaders';

function GLManager(data) {
    const canvasMultiplier = 1;
    this.totalEntries = data.length;
    this.loadedEntries = 0;

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);
    camera.position.z = 5;
    this.camera = camera;
    this.speed = 0.1;

    const scene = new THREE.Scene();
    camera.lookAt = scene.position;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setSize(window.innerWidth * canvasMultiplier, window.innerHeight * canvasMultiplier);
    renderer.setPixelRatio(window.devicePixelRatio);

    this.render = this.render.bind(this);
    this.textures = data.map((entry, i) => {
        const { media } = entry;
        if (!media) {
            return;
        }

        // Video Texture
        if (media.type === 'video') {
            const video = document.createElement('video');
            video.autoplay = true;
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            const that = this;
            video.oncanplay = function() {
                video.play();
                const texture = new THREE.VideoTexture(video);

                texture.format = THREE.RGBAFormat;
                that.calculateAspectRatioFactor(i, texture, video);
                this.oncanplay = null;
                return texture;
            };

            video.src = media.url;
            const texture = new THREE.VideoTexture(video);
            texture.format = THREE.RGBAFormat;
            // this.calculateAspectRatioFactor(i, texture);
            // that.calculateAspectRatioFactor(i, texture, video);

            return texture; // eslint-disable-line
        }

        // Image Texture
        const texture = new THREE.TextureLoader().load(media.url, this.calculateAspectRatioFactor.bind(this, i));
        texture.premultiplyAlpha = true; // Let's make the alpha channel work.
        return texture; // eslint-disable-line
    });

    this.factors = data.map((d) => new THREE.Vector2(1, 1));
    this.currentIndex = 0;
    this.nextIndex = 0;
    this.textureProgress = 0;
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    this.initialRender = false;
    this.time = 0;
    this.loopRaf = null;
    this.loop = this.loop.bind(this);
    this.slowStop = this.slowStop.bind(this);
}

GLManager.prototype.getViewSize = function() {
    const fovInRadians = (this.camera.fov * Math.PI) / 180;
    const viewSize = Math.abs(
        this.camera.position.z * Math.tan(fovInRadians / 2) * 2.5,
    );

    return viewSize;
};

GLManager.prototype.getPlaneSize = function() {
    const multipliers = {
        width: 1.8,
        height: 1.8,
    };

    const viewSize = this.getViewSize();
    return { width: viewSize * multipliers.width, height: viewSize * multipliers.height };
};

GLManager.prototype.calculateAspectRatioFactor = function(index, texture, video = null) {
    const plane = this.getPlaneSize();
    const windowRatio = window.innerWidth / window.innerHeight;
    const rectRatio = (plane.width / plane.height) * windowRatio;
    let imageRatio = (texture.image.width / texture.image.height);

    if (video) {
        console.log(video.videoWidth);
        imageRatio = video.videoWidth / video.videoHeight;
    }

    // console.log(`Rect Ratio: ${rectRatio}`);
    // console.log(`Image Ratio: ${imageRatio}`);
    // console.log(`Window Ratio: ${windowRatio}`);

    let factorX = 1;
    let factorY = 1;
    if (rectRatio <= 1) {
        console.log('Portrait');
        factorX = ((1 * rectRatio) / imageRatio);
        factorY = 1;
    } else {
        console.log('Landscape');
        const multi = windowRatio - imageRatio;
        factorX = ((0.8 * rectRatio) / imageRatio);
        factorY = 0.8;

        console.log({
            windowRatio,
            imageRatio,
            difference: windowRatio - imageRatio,
        });

        if (windowRatio - imageRatio < 0) {
            console.log('Fits');
        } else {
            console.log('DOESNT FIT');
        }
    }

    this.factors[index] = new THREE.Vector2(factorX, factorY);
    if (this.currentIndex === index) {
        this.mesh.material.uniforms.u_textureFactor.value = this.factors[index];
        this.mesh.material.uniforms.u_textureFactor.needsUpdate = true;
    }
    if (this.nextIndex === index) {
        this.mesh.material.uniforms.u_texture2Factor.value = this.factors[index];
        this.mesh.material.uniforms.u_texture2Factor.needsUpdate = true;
    }
    if (this.initialRender) {
        this.loadedEntries++; // eslint-disable-line
        if (this.loadedEntries === this.totalEntries) {
            document.body.classList.remove('loading');
        }
        this.render();
    }
};

// Plane Stuff
GLManager.prototype.createPlane = function() {
    // Calculate bas of Isoceles triangle(camera)
    const viewSize = this.getViewSize();
    const { width, height } = this.getPlaneSize();

    const segments = 120;
    const geometry = new THREE.PlaneBufferGeometry(
        width / 2,
        height / 2,
        segments,
        segments,
    );

    const material = new THREE.ShaderMaterial({
        uniforms: {
            u_texture: { type: 't', value: this.textures[this.currentIndex] },
            u_textureFactor: { type: 'f', value: this.factors[this.currentIndex] },
            u_texture2: { type: 't', value: this.textures[this.nextIndex] },
            u_texture2Factor: { type: 'f', value: this.factors[this.nextIndex] },
            u_textureProgress: { type: 'f', value: this.textureProgress },
            u_offset: { type: 'f', value: 8 },
            u_progress: { type: 'f', value: 0 },
            u_direction: { type: 'f', value: 1 },
            u_effect: { type: 'f', value: 0 },
            u_time: { type: 'f', value: this.time },
            u_waveIntensity: { type: 'f', value: 0 },
            intensity: {
                value: 0.3, type: 'f', min: 0.0, max: 2,
            },
            u_resolution: {
                type: 'v2',
                value: new THREE.Vector2(window.innerWidth, window.innerHeight),
            },
            u_rgbPosition: {
                type: 'v2',
                value: new THREE.Vector2(window.innerWidth / 2, window.innerHeight / 2),
            },
            u_rgbVelocity: { type: 'v2', value: new THREE.Vector2(0, 0) },
        },
        vertexShader: vertex,
        fragmentShader: fragment,
        side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    this.scene.add(mesh);
    // this.scene.add( mesh1);
    this.mesh = mesh;
};

GLManager.prototype.updateTexture = function(newIndex, progress) {
    let didChange = false;
    if (newIndex != null && this.newIndex !== this.currentIndex) {
        this.currentIndex = this.nextIndex;
        this.nextIndex = newIndex;
        this.textureProgress = 0;
        this.mesh.material.uniforms.u_textureProgress.value = 0;
        this.mesh.material.uniforms.u_texture.value = this.textures[this.currentIndex];
        this.mesh.material.uniforms.u_textureFactor.value = this.factors[this.currentIndex];
        this.mesh.material.uniforms.u_texture2.value = this.textures[newIndex];

        this.mesh.material.uniforms.u_texture2Factor.value = this.factors[newIndex];

        didChange = true;
    }
    if (progress != null && progress !== this.textureProgress) {
        this.mesh.material.uniforms.u_textureProgress.value = progress;
        this.textureProgress = progress;
        didChange = true;
    }

    if (!this.loopRaf && didChange) {
        this.render();
    }
};

GLManager.prototype.updateStickEffect = function({
    progress,
    direction,
    waveIntensity,
}) {
    this.mesh.material.uniforms.u_progress.value = progress;
    this.mesh.material.uniforms.u_direction.value = direction;
    this.mesh.material.uniforms.u_waveIntensity.value = waveIntensity;
};

GLManager.prototype.updateRgbEffect = function({ position, velocity }) {
    this.mesh.material.uniforms.u_rgbPosition.value = new THREE.Vector2(
        position.x,
        position.y,
    );
    this.mesh.material.uniforms.u_rgbVelocity.value = new THREE.Vector2(
        velocity.x,
        velocity.y,
    );

    if (!this.loopRaf) {
        this.render();
    }
};
// Other stuff
GLManager.prototype.render = function() {
    if (!this.initialRender) {
        this.initialRender = true;
    }
    this.renderer.render(this.scene, this.camera);
};

GLManager.prototype.mount = function(container) {
    container.prepend(this.renderer.domElement);
};

GLManager.prototype.unmount = function() {
    this.mesh.material.dispose();
    this.mesh.geometry.dispose();
    this.mesh = null;
    this.renderer = null;
    this.camera = null;
    this.scene = null;
    this.container = null;
};

GLManager.prototype.onResize = function() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.mesh.material.uniforms.u_resolution.value = new THREE.Vector2(
        window.innerWidth,
        window.innerHeight,
    );
    // this.camera.aspect = window.inenrWidth / window.innerHeight;
    // this.camera.updateProjectionMatrix();
    for (let i = 0; i < this.textures.length; i++) {
        if (this.textures[i].image) {
            const { image } = this.textures[i];
            const video = image.videoHeight ? image : null;
            this.calculateAspectRatioFactor(i, this.textures[i], video);
        }
    }

    this.render();
};

GLManager.prototype.scheduleLoop = function() {
    if (this.loopRaf) {
        return;
    }

    // this.loop();
    // gsap.ticker.fps(60);
    gsap.ticker.add(this.loop);
};

/**
 * Loops the render function.
 */
GLManager.prototype.loop = function() {
    this.render();
    this.time += this.speed;
    this.mesh.material.uniforms.u_time.value = this.time;
};

GLManager.prototype.slowStop = function() {
    if (this.speed === 0 || this.speed < 0) {
        // this.cancelLoop();
        gsap.ticker.remove(this.loop);
        gsap.ticker.remove(this.slowStop);
        return;
    }

    this.render();
    this.speed -= 0.008;
    this.time += this.speed;
    this.mesh.material.uniforms.u_time.value = this.time;
    console.log('Slow stop');

    // this.loopRaf = requestAnimationFrame(this.slowStop);

    gsap.ticker.add(this.slowStop);
};

/**
 * Cancel the animation loop.
 */
GLManager.prototype.cancelLoop = function() {
    // cancelAnimationFrame(this.loopRaf);
    gsap.ticker.remove(this.loop);
    gsap.ticker.remove(this.slowStop);
    // this.loopRaf = null;
};

export { GLManager };
